import { FileDownloadOutlined as FileDownloadOutlinedIcon } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { isEmpty, map } from 'lodash';
import { Fragment, useCallback, useState } from 'react';
import { UseMutateFunction } from 'react-query';

import { CloseFormikDialogResult } from 'components/Dialog';
import { useQueryFilters, useQuerySorting } from 'hooks';
import { Sorting } from 'types';

import { ExportDataDialog } from './ExportDataDialog';

type Props<Tab, Payload> = {
  tab: Tab;
  columns: string[];
  isLoading: boolean;
  labelGetter: (item: string) => string;
  onExport: UseMutateFunction<
    void,
    unknown,
    {
      data: Payload;
      filters: string;
      sorting: Sorting;
    },
    unknown
  >;
};

export const ExportDataButton = <Tab, Payload>({
  tab,
  columns,
  isLoading,
  labelGetter,
  onExport,
}: Props<Tab, Payload>) => {
  const [open, setOpen] = useState(false);
  const { filtersQuery } = useQueryFilters();
  const { sorting } = useQuerySorting();

  const handleClose = useCallback(
    (result: CloseFormikDialogResult<{ columns: string[] }>) => {
      if (result.ok && result.data) {
        const values = {
          columns: map(result.data.values.columns, (column) => ({
            name: column,
            label: labelGetter(column),
          })),
        };
        onExport(
          {
            data: { ...values, state: tab } as Payload,
            filters: filtersQuery,
            sorting,
          },
          {
            onSuccess: () => {
              setOpen(false);
            },
          },
        );
      } else {
        setOpen(false);
      }
    },
    [filtersQuery, sorting, labelGetter, onExport, tab],
  );

  if (isEmpty(columns)) {
    return null;
  }

  return (
    <Fragment>
      <IconButton onClick={() => setOpen(true)}>
        <FileDownloadOutlinedIcon />
      </IconButton>
      <ExportDataDialog
        open={open}
        columns={columns}
        isLoading={isLoading}
        labelGetter={labelGetter}
        onClose={handleClose}
      />
    </Fragment>
  );
};
